<template>
  <div class="edit-stories-page">
    <v-container class="py-8">
      <!-- Header -->
      <div class="page-header mb-3">
        <div class="brand-header">
          <router-link to="/">
            <PassingOnLogo class="brand-logo-svg" :name2-override="memorial?.nameOfPerson ? '-' + memorial.nameOfPerson : null" />
          </router-link>
        </div>

        <!-- Profile Menu (Top Right) -->
        <ProfileMenu />
      </div>

      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h6 section-header primary--text">
              Edit Stories
            </v-card-title>

            <v-card-text class="pa-6">
              <!-- Instructions -->
              <div class="mb-6">
                <p class="text-body-1 mb-4">
                  Share some meaningful stories about {{ memorial?.nameOfPerson || 'your loved one' }}
                </p>
              </div>

              <!-- Stories List -->
              <div
                v-for="(story, index) in stories"
                :key="index"
                class="story-item mb-4"
              >
                <v-textarea
                  v-model="stories[index].text"
                  :label="`Story ${index + 1}`"
                  variant="outlined"
                  rows="4"
                  auto-grow
                  class="mb-2"
                ></v-textarea>

                <div class="story-actions">
                  <v-btn
                    v-if="stories.length > 1"
                    color="error"
                    variant="text"
                    size="small"
                    @click="removeStory(index)"
                  >
                    <v-icon left>mdi-delete</v-icon>
                    Remove Story
                  </v-btn>
                </div>
              </div>

              <v-btn
                color="primary"
                variant="outlined"
                @click="addStory"
                class="mt-2"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Another Story
              </v-btn>

              <div v-if="stories.length === 0" class="text-center py-8 text--secondary">
                <v-icon size="64" color="grey-lighten-1" class="mb-3">mdi-book-open-page-variant</v-icon>
                <p>No stories yet. Click "Add Another Story" to get started.</p>
              </div>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="cancel"
                class="mr-4"
              >
                Back to Dashboard
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                :disabled="saving"
                :loading="saving"
                @click="saveStories"
                class="px-8"
              >
                Save
                <v-icon right class="ml-2">mdi-content-save</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Error Dialog -->
    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Error
        </v-card-title>
        <v-card-text>
          <p>{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'
import ProfileMenu from '../components/ProfileMenu.vue'

const router = useRouter()
const store = useMemorialStore()

// Refs
const memorial = ref(null)

// State
const stories = ref([{ text: '', uuid: null, isExisting: false }])
const saving = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')

// Stories to delete (track UUIDs)
const storiesToDelete = ref([])

// Load memorial data
const loadMemorialData = async () => {
  try {
    const response = await apiService.getMemorial(store.memorialUuid)
    if (response.success && response.memorial) {
      memorial.value = response.memorial

      // Load existing stories
      if (response.memorial.stories && response.memorial.stories.length > 0) {
        stories.value = response.memorial.stories.map(story => ({
          uuid: story.uuid,
          text: story.text || '',
          isExisting: true
        }))
      }
    }
  } catch (err) {
    console.error('Error loading memorial:', err)
  }
}

const addStory = () => {
  stories.value.push({ text: '', uuid: null, isExisting: false })
}

const removeStory = (index) => {
  const story = stories.value[index]

  // If it's an existing story, track it for deletion
  if (story.isExisting && story.uuid) {
    storiesToDelete.value.push(story.uuid)
  }

  // Remove from stories array
  stories.value.splice(index, 1)

  // Ensure at least one story remains
  if (stories.value.length === 0) {
    stories.value.push({ text: '', uuid: null, isExisting: false })
  }
}

const saveStories = async () => {
  saving.value = true

  try {
    // Filter out empty stories
    const validStories = stories.value.filter(story => story.text && story.text.trim().length > 0)

    // Update/create stories via API
    const response = await apiService.updateMemorialStories(
      store.memorialUuid,
      {
        stories: validStories.map(story => ({
          uuid: story.uuid,
          text: story.text
        })),
        deleteStoryUuids: storiesToDelete.value
      },
      store.apiToken
    )

    if (response.success) {
      console.log('Stories saved successfully')
      router.push('/memory/dashboard')
    } else {
      throw new Error(response.msg || 'Failed to save stories')
    }
  } catch (err) {
    console.error('Error saving stories:', err)
    errorMessage.value = 'Error saving stories: ' + (err.message || 'Please try again.')
    showErrorDialog.value = true
  } finally {
    saving.value = false
  }
}

const cancel = () => {
  router.push('/memory/dashboard')
}

onMounted(() => {
  loadMemorialData()
})
</script>

<style scoped>
.edit-stories-page {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--v-theme-background) 0%, var(--v-theme-surface) 100%);
}

.main-card {
  border-radius: 12px;
  overflow: hidden;
}

.story-item {
  border: 1px solid var(--v-theme-neutral200);
  border-radius: 8px;
  padding: 16px;
  background: white;
}

.story-actions {
  display: flex;
  justify-content: flex-start;
}

.page-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* symmetric gutters reserve room for the profile menu pinned to the right, so
     a long memorial name truncates before it can run underneath the icon */
  padding-left: 64px;
  padding-right: 64px;
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
}

/* The router-link wrapper has to be shrinkable too, otherwise the name never
   truncates; flex also removes the inline baseline gap under the brand text
   that was pushing the profile icon out of alignment with the title. */
.brand-header > a {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.brand-logo {
  height: 40px;
  width: auto;
}

.brand-title {
  letter-spacing: 0.1rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  margin: 0;
}

.profile-menu-wrapper {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

.section-header {
  font-weight: 600 !important;
  border-bottom: 1px solid var(--v-theme-border);
}
</style>
